/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { t } from '@sidekick/copy';
import { appPalette } from '@sidekick/design-tokens';
import { signOut } from 'aws-amplify/auth';
import { useState } from 'react';
import { PageHeader } from '../components/PageHeader.js';
import { createGoogleOauthState } from '../lib/googleOauth.js';
import { inputStyle, localTimezone, monoLabel } from '../lib/ui.js';
import { trpc } from '../lib/trpc.js';

const p = appPalette;

/**
 * The Account — administrative settings.
 *
 * Identity and durable context now live on The Constitution; this page
 * holds account-level settings: who you're signed in as, when the
 * account was created, your timezone, and the way out.
 */
export function AccountPage({
  calendarJustConnected = false,
}: {
  calendarJustConnected?: boolean;
}) {
  const info = trpc.account.info.useQuery();
  const utils = trpc.useUtils();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const onExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const data = await utils.account.export.fetch();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `second-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const memberSince =
    info.data != null
      ? new Date(info.data.memberSince).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : null;
  const timezone = info.data?.timezone ?? localTimezone();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader label={t.nav.account} />

      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '40px 22px' }}>
        <div
          style={{
            width: '100%',
            maxWidth: 520,
            display: 'flex',
            flexDirection: 'column',
            gap: 28,
          }}
        >
          <p
            style={{
              fontFamily: 'var(--font-serif)',
              fontFeatureSettings: 'var(--serif-feat)',
              fontSize: 15,
              lineHeight: 1.55,
              color: p.muted,
              margin: 0,
            }}
          >
            Your account settings live here.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <InfoRow
              label="Signed in as"
              value={info.data?.email ?? (info.isPending ? '…' : '—')}
            />
            <InfoRow label="Member since" value={memberSince ?? (info.isPending ? '…' : '—')} />
            <InfoRow label="Timezone" value={timezone} />
          </div>

          {info.error ? (
            <span style={{ ...monoLabel, color: p.accent }}>{info.error.message}</span>
          ) : null}

          <div
            style={{
              borderTop: `1px solid ${p.line}`,
              paddingTop: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <span style={monoLabel}>Your data</span>
            <p
              style={{
                fontFamily: 'var(--font-serif)',
                fontFeatureSettings: 'var(--serif-feat)',
                fontSize: 14,
                color: p.muted,
                margin: 0,
              }}
            >
              Download everything your Second holds for you — goals, projects, actions, captures,
              stakeholders, wins, reviews, conversations, and briefs — as a JSON file.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <button
                type="button"
                onClick={() => {
                  void onExport();
                }}
                disabled={exporting}
                style={{
                  padding: '10px 18px',
                  background: 'transparent',
                  color: p.fg,
                  border: `1px solid ${p.line}`,
                  fontSize: 13,
                  cursor: exporting ? 'wait' : 'pointer',
                  opacity: exporting ? 0.6 : 1,
                  alignSelf: 'flex-start',
                }}
              >
                {exporting ? 'Preparing…' : 'Export my data →'}
              </button>
              {exportError ? (
                <span style={{ ...monoLabel, color: p.accent }}>{exportError}</span>
              ) : null}
            </div>
          </div>

          <GoogleCalendarBlock justConnected={calendarJustConnected} />

          <div style={{ borderTop: `1px solid ${p.line}`, paddingTop: 24 }}>
            <button
              type="button"
              onClick={() => {
                void signOut();
              }}
              style={{
                padding: '10px 18px',
                background: 'transparent',
                color: p.fg,
                border: `1px solid ${p.line}`,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {t.auth.signOut} →
            </button>
          </div>

          <DangerZone email={info.data?.email ?? null} />
        </div>
      </div>
    </div>
  );
}

const serifParagraph: React.CSSProperties = {
  fontFamily: 'var(--font-serif)',
  fontFeatureSettings: 'var(--serif-feat)',
  fontSize: 14,
  color: p.muted,
  margin: 0,
};

function GoogleCalendarBlock({ justConnected }: { justConnected: boolean }) {
  const utils = trpc.useUtils();
  const status = trpc.calendar.status.useQuery();
  const connect = trpc.calendar.connectGoogleCalendar.useMutation({
    onSuccess: ({ authUrl }) => {
      window.location.assign(authUrl);
    },
  });
  const disconnect = trpc.calendar.disconnect.useMutation({
    onSuccess: () => {
      void utils.calendar.status.invalidate();
    },
  });

  const onConnect = () => {
    connect.mutate({ state: createGoogleOauthState() });
  };

  const s = status.data;
  const error =
    connect.error?.message ?? disconnect.error?.message ?? status.error?.message ?? null;

  return (
    <div
      style={{
        borderTop: `1px solid ${p.line}`,
        paddingTop: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <span style={monoLabel}>Google Calendar</span>

      {justConnected ? (
        <p style={{ ...serifParagraph, color: p.fg }}>Google Calendar is connected.</p>
      ) : null}

      {s == null ? (
        <span style={monoLabel}>{status.isPending ? '…' : '—'}</span>
      ) : s.connected ? (
        <>
          {s.needsReauth ? (
            <div
              style={{
                border: `1px solid ${p.accent}`,
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <span style={{ ...monoLabel, color: p.accent }}>Reconnect needed</span>
              <p style={serifParagraph}>
                Google needs you to sign in again before your Second can keep reading your calendar.
              </p>
              <ConnectButton onConnect={onConnect} pending={connect.isPending} />
            </div>
          ) : null}
          <span style={{ ...serifParagraph, fontSize: 15, color: p.fg }}>
            {s.googleAccountEmail != null ? `Connected as ${s.googleAccountEmail}` : 'Connected'}
          </span>
          <span style={serifParagraph}>
            {s.lastSyncAt != null
              ? `Last synced ${relativeTime(new Date(s.lastSyncAt))}`
              : 'Hasn’t synced yet.'}
          </span>
          <button
            type="button"
            aria-label="Disconnect Google Calendar"
            onClick={() => disconnect.mutate()}
            disabled={disconnect.isPending}
            style={{
              padding: '10px 18px',
              background: 'transparent',
              color: p.fg,
              border: `1px solid ${p.line}`,
              fontSize: 13,
              cursor: disconnect.isPending ? 'wait' : 'pointer',
              opacity: disconnect.isPending ? 0.6 : 1,
              alignSelf: 'flex-start',
            }}
          >
            {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
          </button>
        </>
      ) : (
        <>
          <p style={serifParagraph}>
            Connect your Google Calendar so your Second can read your day — read-only, nothing is
            ever written to it.
          </p>
          <ConnectButton onConnect={onConnect} pending={connect.isPending} />
        </>
      )}

      {error ? <span style={{ ...monoLabel, color: p.accent }}>{error}</span> : null}
    </div>
  );
}

function ConnectButton({ onConnect, pending }: { onConnect: () => void; pending: boolean }) {
  return (
    <button
      type="button"
      aria-label="Connect Google Calendar"
      onClick={onConnect}
      disabled={pending}
      style={{
        padding: '10px 18px',
        background: 'transparent',
        color: p.fg,
        border: `1px solid ${p.line}`,
        fontSize: 13,
        cursor: pending ? 'wait' : 'pointer',
        opacity: pending ? 0.6 : 1,
        alignSelf: 'flex-start',
      }}
    >
      {pending ? 'Opening Google…' : 'Connect Google Calendar →'}
    </button>
  );
}

function relativeTime(d: Date): string {
  const diffSec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'yesterday';
  return `${diffDay}d ago`;
}

function DangerZone({ email }: { email: string | null }) {
  const del = trpc.account.delete.useMutation({
    onSuccess: () => {
      void signOut();
    },
  });
  const [armed, setArmed] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const canDelete =
    email != null && confirmText.trim().toLowerCase() === email.toLowerCase() && !del.isPending;

  return (
    <div
      style={{
        borderTop: `1px solid ${p.accent}`,
        paddingTop: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <span style={{ ...monoLabel, color: p.accent }}>Danger zone</span>
      <p
        style={{
          fontFamily: 'var(--font-serif)',
          fontFeatureSettings: 'var(--serif-feat)',
          fontSize: 14,
          color: p.muted,
          margin: 0,
        }}
      >
        Deleting your account permanently removes all your data and signs you out for good. This
        cannot be undone — export your data first if you want a copy.
      </p>

      {armed ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            type="email"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={email ?? 'your email'}
            aria-label="Type your email to confirm"
            style={{ ...inputStyle }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button
              type="button"
              onClick={() => del.mutate({ confirmEmail: confirmText.trim() })}
              disabled={!canDelete}
              style={{
                padding: '10px 18px',
                background: canDelete ? p.accent : 'transparent',
                color: canDelete ? p.bg : p.muted,
                border: `1px solid ${p.accent}`,
                fontSize: 13,
                cursor: canDelete ? 'pointer' : 'not-allowed',
                opacity: del.isPending ? 0.6 : 1,
              }}
            >
              {del.isPending ? 'Deleting…' : 'Permanently delete'}
            </button>
            <button
              type="button"
              onClick={() => {
                setArmed(false);
                setConfirmText('');
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: p.muted,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
          {del.error ? (
            <span style={{ ...monoLabel, color: p.accent }}>{del.error.message}</span>
          ) : null}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setArmed(true)}
          style={{
            padding: '10px 18px',
            background: 'transparent',
            color: p.accent,
            border: `1px solid ${p.accent}`,
            fontSize: 13,
            cursor: 'pointer',
            alignSelf: 'flex-start',
          }}
        >
          Delete my account
        </button>
      )}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={monoLabel}>{label}</span>
      <span
        style={{
          fontFamily: 'var(--font-serif)',
          fontFeatureSettings: 'var(--serif-feat)',
          fontSize: 15,
          color: p.fg,
        }}
      >
        {value}
      </span>
    </div>
  );
}
